/**
 * The "Your website" sheet and the status chip beside the address, as pure rules.
 *
 * The owner could not tell whether their site was up, and could not find how to take it down or
 * put it back. The editor now shows one word from the SERVER — Live, Password or Offline — and
 * tapping it opens this sheet, which offers the way out of whichever state the page is in. The
 * screen maps each key to its action; what is offered, and in which words, is decided here so it
 * can be tested without rendering (this repo's jest has no React Native renderer).
 *
 * The words are the ones the web builder uses for the same things, so an owner who uses both
 * never has to translate between them.
 */
import { displayAddress } from '@/webapp/siteAddress';

/** What the server says about a page, as the editor shows it. */
export type SiteStatus = 'live' | 'password' | 'offline';

export const SITE_STATUS_LABEL: Record<SiteStatus, string> = {
  live: 'Live',
  password: 'Password',
  offline: 'Offline',
};

/**
 * The fields of the server's listing that decide whether a page is served.
 *
 * `takenDown` is what the server sends (a boolean, never the moderator); `takenDownBy` is kept
 * for a listing that carries the raw row, as the mock backend's does.
 */
export interface SiteListing {
  webPublished?: boolean;
  webVisibility?: 'public' | 'password';
  takenDown?: boolean;
  takenDownBy?: string;
}

/**
 * The server's listing, as one of three words.
 *
 * Only ever called with an ANSWER from the server. A lookup that failed is not "offline" — it is
 * nothing known — and the screen shows no chip at all for it, rather than a guess (see
 * publish-state-source-of-truth: a privacy state must never be inferred from a missing answer).
 *
 * `null` IS an answer: the server says it has no such app, so nothing is served at the address,
 * and Offline is the truth. An absent `webPublished` reads as offline too, which is the side that
 * cannot tell an owner their page is private when it is not: the page is served only when the
 * server says `webPublished: true` and no moderator has taken it down (`pageIsUp`,
 * infra/lambda/shared/siteGate.ts). A served page is behind a password only when it says
 * `'password'`; absent visibility is public, exactly as the site handler serves it.
 */
export function siteStatusOf(listing: SiteListing | null): SiteStatus {
  if (!listing || listing.webPublished !== true || listing.takenDown || listing.takenDownBy) return 'offline';
  return listing.webVisibility === 'password' ? 'password' : 'live';
}

export type WebsiteActionKey =
  | 'copy'
  | 'open'
  | 'share'
  | 'setPassword'
  | 'makePublic'
  | 'takeOffline'
  | 'putBackOnline'
  | 'putBackWithPassword';

export interface WebsiteAction {
  key: WebsiteActionKey;
  label: string;
  destructive?: boolean;
}

/**
 * What the sheet offers for a page in this state — always including the way back from it.
 *
 * The sheet used to exist only for a live page. A page taken offline got "Publish to web —
 * Public / Password-protected", which reads as making a new website rather than restoring this
 * one, and nothing on screen said the page was offline in the first place.
 *
 * An offline page is offered its password route as well as the public one. Taking a page offline
 * clears its password on the server (`unpublishWeb`), so "Put it back online" can only restore it
 * as PUBLIC — and a page that was behind a password must not be made public by an owner who only
 * meant to undo "Take offline".
 */
export function websiteActions(status: SiteStatus): WebsiteAction[] {
  if (status === 'offline') {
    return [
      { key: 'putBackOnline', label: 'Put it back online' },
      { key: 'putBackWithPassword', label: 'Put it back with a password' },
    ];
  }
  return [
    { key: 'copy', label: 'Copy link' },
    { key: 'open', label: 'Open' },
    { key: 'share', label: 'Share' },
    status === 'password'
      ? { key: 'makePublic', label: 'Make public' }
      : { key: 'setPassword', label: 'Set a password' },
    { key: 'takeOffline', label: 'Take offline', destructive: true },
  ];
}

/** The sheet's message: where the page lives, and what that state means for a visitor. */
export function websiteSheetMessage(status: SiteStatus, url: string): string {
  const meaning =
    status === 'live'
      ? 'Live — anyone with the link can open it.'
      : status === 'password'
        ? 'Password — visitors need the password to open it.'
        : 'Offline — anyone who opens the link is told it isn’t available.';
  return `${displayAddress(url)}\n\n${meaning}`;
}

/**
 * Asked before a page is taken down.
 *
 * "Take offline" was one tap inside an alert full of harmless buttons, beside Copy link and
 * Share, and it acts at once on a page other people may be using. The confirmation says what a
 * visitor will see and that nothing is lost — the server only switches the page off
 * (`unpublishWeb`); the app and what people saved in it stay.
 */
export function takeOfflineConfirmation(appName: string): { title: string; body: string; confirm: string } {
  return {
    title: `Take ${appName} offline?`,
    body: 'Anyone who opens its link will be told it isn’t available. Your app and everything people saved in it stay as they are, and Your website puts it back online.',
    confirm: 'Take offline',
  };
}
